// AiUsageOverview — pen H-05: five figures for the period (cost, tokens,
// cache hit rate, response time, errors), cost or tokens per day, and the
// biggest spenders by workspace, user, model or call type. A breakdown row
// opens the call list filtered to it; "see calls" on errors opens the
// failed calls. Pure projection of the summary read.

export { AiUsageOverview };

import type {
    AiUsageBreakdownBy,
    AiUsageSummary,
} from "@bilda/app/agentCallAdmin";
import { Remote } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { useState } from "react";
import {
    breakdownLabel,
    cacheHitRate,
    callTypeLabel,
    exactMoney,
    formatCount,
    formatDay,
    formatInt,
    formatMoney,
    formatPercent,
    formatSeconds,
    moneyIsAbbreviated,
    totalTokens,
} from "../../app/aiUsage/format.js";
import { type AiUsageRoute, withFilter } from "../../app/aiUsage/route.js";
import { Icon } from "../Icon/Icon.js";
import { SegmentedControl } from "../SegmentedControl/SegmentedControl.js";
import type { AiUsageRead } from "./AiUsagePage.js";
import {
    Abbreviated,
    LoadFailed,
    NoCalls,
    OverviewSkeleton,
} from "./AiUsageStates.js";
// The shared frame styles first, so this view's rules win ties.
import "./AiUsage.css";
import "./AiUsageOverview.css";

type Props = {
    readonly route: AiUsageRoute;
    readonly summary: AiUsageRead<AiUsageSummary>;
    readonly onNavigate: (route: AiUsageRoute) => void;
    readonly onRetry: () => void;
};

const Figure = ({
    label,
    value,
    exact,
    serif = true,
    children,
}: {
    readonly label: string;
    readonly value: string;
    readonly exact?: string;
    readonly serif?: boolean;
    readonly children?: React.ReactNode;
}) => (
    <section className="ai-usage__figure">
        <h2 className="ai-usage__figure-label">{label}</h2>
        <p
            className={`ai-usage__figure-value${serif ? "" : " ai-usage__figure-value--plain"}`}
        >
            <Abbreviated shown={value} exact={exact} />
        </p>
        <div className="ai-usage__figure-note">{children}</div>
    </section>
);

const Figures = ({
    s,
    onErrors,
}: {
    readonly s: AiUsageSummary;
    readonly onErrors: () => void;
}) => {
    const { t, i18n } = useLingui();
    const loc = i18n.locale;
    const tt = s.totals;
    const tokens = totalTokens(tt);
    const inTokens = tt.inputTokens + tt.cacheReadTokens + tt.cacheWriteTokens;
    const hit = cacheHitRate(tt);
    const change =
        s.previousCostMicroUsd === 0
            ? undefined
            : (tt.costMicroUsd - s.previousCostMicroUsd) /
              s.previousCostMicroUsd;
    const errorShare = tt.calls === 0 ? 0 : tt.failed / tt.calls;
    return (
        <div className="ai-usage__figures">
            <Figure
                label={t`Cost`}
                value={formatMoney(loc, tt.costMicroUsd)}
                {...(moneyIsAbbreviated(tt.costMicroUsd)
                    ? { exact: exactMoney(loc, tt.costMicroUsd) }
                    : {})}
            >
                <span>
                    {change === undefined
                        ? t`No cost in the period before`
                        : t`${change >= 0 ? "+" : ""}${formatPercent(loc, change)} vs the period before`}
                </span>
                {tt.unpricedCalls > 0 ? (
                    <span className="ai-usage__warn">
                        {t`${formatInt(loc, tt.unpricedCalls)} calls have no price`}
                    </span>
                ) : null}
            </Figure>
            <Figure
                label={t`Tokens`}
                value={formatCount(loc, tokens)}
                exact={formatInt(loc, tokens)}
            >
                {t`In ${formatCount(loc, inTokens)} · out ${formatCount(loc, tt.outputTokens)}`}
            </Figure>
            <Figure
                label={t`Cache hit rate`}
                value={hit === undefined ? "—" : formatPercent(loc, hit)}
            >
                {t`Share of input read from cache`}
            </Figure>
            <Figure
                label={t`Response time`}
                value={
                    tt.medianDurationMs === null
                        ? "—"
                        : formatSeconds(loc, tt.medianDurationMs)
                }
            >
                {t`Median · slowest 5%: ${tt.p95DurationMs === null ? "—" : formatSeconds(loc, tt.p95DurationMs)} · first token ${tt.medianTtftMs === null ? "—" : formatSeconds(loc, tt.medianTtftMs)}`}
            </Figure>
            <Figure label={t`Errors`} value={formatPercent(loc, errorShare)}>
                {tt.failed === 0 ? (
                    t`No failed calls`
                ) : (
                    <button
                        type="button"
                        className="ai-usage__link ai-usage__link--danger"
                        onClick={onErrors}
                    >
                        {t`${formatInt(loc, tt.failed)} of ${formatInt(loc, tt.calls)} calls · see calls`}
                        <Icon name="arrow-right" size="xs" />
                    </button>
                )}
            </Figure>
        </div>
    );
};

type ChartMetric = "cost" | "tokens";

const DayChart = ({ s }: { readonly s: AiUsageSummary }) => {
    const { t, i18n } = useLingui();
    const loc = i18n.locale;
    // View-ephemeral: which measure the bars show. The pointed-at bar and
    // its tip are pure CSS (:hover).
    const [metric, setMetric] = useState<ChartMetric>("cost");
    const measureOf = (d: AiUsageSummary["days"][number]): number =>
        metric === "cost" ? d.costMicroUsd : d.tokens;
    const max = Math.max(1, ...s.days.map(measureOf));
    const last = s.days.length - 1;
    const tick = (i: number): boolean =>
        i === 0 || i === last || ((i + 1) % 5 === 0 && last - i > 1);
    return (
        <section className="ai-usage__card">
            <div className="ai-usage__card-head">
                <h2 className="ai-usage__card-title">
                    {metric === "cost" ? t`Cost per day` : t`Tokens per day`}
                </h2>
                <SegmentedControl
                    size="sm"
                    options={[
                        { value: "tokens", label: t`Tokens` },
                        { value: "cost", label: t`Cost` },
                    ]}
                    value={metric}
                    onChange={(v) =>
                        setMetric(v === "tokens" ? "tokens" : "cost")
                    }
                    ariaLabel={t`Chart measure`}
                />
            </div>
            <div className="ai-usage__chart">
                {s.days.map((d) => {
                    const label = t`${formatDay(loc, d.day)} · ${formatMoney(loc, d.costMicroUsd)} · ${formatCount(loc, d.tokens)} tokens`;
                    return (
                        <div key={d.day} className="ai-usage__bar-slot">
                            <span
                                className="ai-usage__bar-tip"
                                aria-hidden="true"
                            >
                                {label}
                            </span>
                            <span
                                className="ai-usage__bar"
                                style={{
                                    height: `${Math.max(measureOf(d) > 0 ? 2 : 0, (measureOf(d) / max) * 100)}%`,
                                }}
                                role="img"
                                aria-label={label}
                            />
                        </div>
                    );
                })}
            </div>
            <div className="ai-usage__axis" aria-hidden="true">
                {s.days.map((d, i) => (
                    <span key={d.day} className="ai-usage__axis-tick">
                        {tick(i)
                            ? i === 0 || i === last
                                ? formatDay(loc, d.day)
                                : d.day.slice(8).replace(/^0/, "")
                            : ""}
                    </span>
                ))}
            </div>
        </section>
    );
};

const SHOWN_ROWS = 5;

const Breakdown = ({
    s,
    route,
    onNavigate,
}: {
    readonly s: AiUsageSummary;
    readonly route: AiUsageRoute;
    readonly onNavigate: (route: AiUsageRoute) => void;
}) => {
    const { t, i18n } = useLingui();
    const loc = i18n.locale;
    // View-ephemeral: past the first five rows.
    const [all, setAll] = useState(false);
    const b = s.breakdown;
    const total = Math.max(1, s.totals.costMicroUsd);
    const rows = all ? b.rows : b.rows.slice(0, SHOWN_ROWS);
    const nameOf = (key: string | null, label: string | null): string => {
        if (key === null)
            return b.by === "user"
                ? t`No user (background)`
                : t`No workspace (shared)`;
        if (b.by === "call_type") {
            const known = callTypeKeys.find((k) => k === key);
            return i18n._(callTypeLabel(known ?? "unknown_site"));
        }
        return label ?? key;
    };
    const open = (key: string | null) => {
        if (key === null) return;
        const base = { ...route, tab: "calls" as const };
        const next =
            b.by === "workspace"
                ? withFilter(base, "workspace", key)
                : b.by === "user"
                  ? withFilter(base, "user", key)
                  : b.by === "model"
                    ? withFilter(base, "model", key)
                    : withFilter(
                          base,
                          "callType",
                          callTypeKeys.find((k) => k === key),
                      );
        onNavigate(next);
    };
    const bys: readonly AiUsageBreakdownBy[] = [
        "workspace",
        "user",
        "model",
        "call_type",
    ];
    return (
        <section className="ai-usage__card">
            <div className="ai-usage__card-head">
                <h2 className="ai-usage__card-title">{t`Top spenders`}</h2>
                <SegmentedControl
                    size="sm"
                    options={bys.map((v) => ({
                        value: v,
                        label: i18n._(breakdownLabel(v)),
                    }))}
                    value={b.by}
                    onChange={(v) => {
                        const by = bys.find((x) => x === v);
                        if (by !== undefined) onNavigate({ ...route, by });
                    }}
                    ariaLabel={t`Break down by`}
                />
            </div>
            <table className="ai-usage__table ai-usage__table--breakdown">
                <thead>
                    <tr>
                        <th scope="col">{t`Name`}</th>
                        <th
                            scope="col"
                            className="ai-usage__num"
                        >{t`Calls`}</th>
                        <th
                            scope="col"
                            className="ai-usage__num"
                        >{t`Tokens`}</th>
                        <th scope="col" className="ai-usage__num">{t`Cost`}</th>
                        <th scope="col">{t`Share`}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const share = r.costMicroUsd / total;
                        const name = nameOf(r.key, r.label);
                        return (
                            <tr
                                key={r.key ?? "none"}
                                className={
                                    r.key === null ? "" : "ai-usage__row--link"
                                }
                                onClick={() => open(r.key)}
                            >
                                <td className="ai-usage__name" title={name}>
                                    {r.key === null ? (
                                        name
                                    ) : (
                                        <button
                                            type="button"
                                            className="ai-usage__row-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                open(r.key);
                                            }}
                                        >
                                            {b.by === "model" ? (
                                                <code>{name}</code>
                                            ) : (
                                                name
                                            )}
                                        </button>
                                    )}
                                </td>
                                <td className="ai-usage__num">
                                    {formatInt(loc, r.calls)}
                                </td>
                                <td className="ai-usage__num">
                                    {formatCount(loc, r.tokens)}
                                </td>
                                <td className="ai-usage__num ai-usage__strong">
                                    {formatMoney(loc, r.costMicroUsd)}
                                </td>
                                <td>
                                    <span className="ai-usage__share">
                                        <span className="ai-usage__share-track">
                                            <span
                                                className="ai-usage__share-fill"
                                                style={{
                                                    width: `${Math.min(100, share * 100)}%`,
                                                }}
                                            />
                                        </span>
                                        <span className="ai-usage__share-pct">
                                            {formatPercent(loc, share)}
                                        </span>
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {b.rows.length > SHOWN_ROWS && !all ? (
                <button
                    type="button"
                    className="ai-usage__link"
                    onClick={() => setAll(true)}
                >
                    {t`Show all ${formatInt(loc, b.totalRows)}`}
                    <Icon name="arrow-right" size="xs" />
                </button>
            ) : null}
        </section>
    );
};

const callTypeKeys = [
    "chat_round",
    "ocr_batch",
    "source_understanding",
    "part_generation",
    "judged_checks",
    "claim_reading",
    "material_repair",
    "journal_summary",
    "activity_summary",
    "material_draft",
    "freeform_structuring",
] as const;

const AiUsageOverview = ({ route, summary, onNavigate, onRetry }: Props) => {
    const { t, i18n } = useLingui();
    const loaded = Remote.match<
        AiUsageSummary,
        unknown,
        AiUsageSummary | undefined
    >({
        notAsked: () => undefined,
        loading: () => undefined,
        reloading: (prev) => prev,
        loaded: (v) => v,
        failed: () => undefined,
    })(summary);
    if (summary._tag === "Remote.Failed")
        return <LoadFailed onRetry={onRetry} />;
    if (loaded === undefined) return <OverviewSkeleton />;
    if (!loaded.anyCalls)
        return (
            <NoCalls onRates={() => onNavigate({ ...route, tab: "rates" })} />
        );
    return (
        <div className="ai-usage__overview">
            {loaded.droppedSinceStart > 0 ? (
                <p className="ai-usage__notice" role="status">
                    <Icon name="warning" size="sm" />
                    {t`${formatInt(i18n.locale, loaded.droppedSinceStart)} calls since the server started could not be written to the log. They are missing from these figures.`}
                </p>
            ) : null}
            <Figures
                s={loaded}
                onErrors={() =>
                    onNavigate(
                        withFilter(
                            { ...route, tab: "calls" },
                            "outcome",
                            "failed",
                        ),
                    )
                }
            />
            <DayChart s={loaded} />
            <Breakdown s={loaded} route={route} onNavigate={onNavigate} />
        </div>
    );
};
